import type { ReactNode } from 'react';
import { Card, Heading } from '@aaimplatform/ui';

/** The words a setting with no stored value reads as. Never a default. */
export const NOT_CONFIGURED = 'Not configured';

export interface SettingRowConfig {
  label: string;
  description: string;
  /** The stored value in words. Omitted means nothing stores it. */
  value?: ReactNode;
}

/**
 * The one section card of the platform-config pages: the kit Card with a
 * section title at a single heading size, an optional intro line, and the
 * section content. System Defaults, Notification Defaults, Lending Defaults,
 * Integration Credentials and Provider Rate Limits each hand-rolled this card
 * and picked their own heading scale (26px, 18px with an icon tile, 20px), so
 * one title read at three sizes across sibling pages.
 */
export function ConfigSection({
  title,
  intro,
  className = '',
  children,
}: {
  title: ReactNode;
  intro?: ReactNode;
  className?: string;
  children: ReactNode;
}) {
  return (
    <Card className={className}>
      <section>
        <Heading level={2} as={3} size="card" className="mb-2">
          {title}
        </Heading>
        {intro && <div className="text-sm font-sans text-secondary leading-normal mb-4">{intro}</div>}
        {children}
      </section>
    </Card>
  );
}

/**
 * A section of read-only setting rows. Each row states its stored value, or
 * "Not configured" when no declared field stores it; a client-side default is
 * never shown in its place.
 */
export function SettingRowsSection({
  title,
  intro,
  rows,
}: {
  title: string;
  intro?: ReactNode;
  rows: SettingRowConfig[];
}) {
  return (
    <ConfigSection title={title} intro={intro} className="mb-6">
      <dl className="divide-y divide-recessed">
        {rows.map((row) => (
          <div key={row.label} className="flex flex-col gap-1 py-3 sm:flex-row sm:items-center sm:justify-between sm:gap-6">
            <dt className="text-sm font-sans font-medium text-primary leading-normal">
              {row.label}
              <span className="block text-xs font-normal text-tertiary mt-1">{row.description}</span>
            </dt>
            <dd className="text-sm font-sans text-secondary sm:text-right">{row.value ?? NOT_CONFIGURED}</dd>
          </div>
        ))}
      </dl>
    </ConfigSection>
  );
}
